import { EditorSettingService } from '@affine/core/modules/editor-setting';
import { FeatureFlagService } from '@affine/core/modules/feature-flag';
import { ThemeEditorService } from '@affine/core/modules/theme-editor';
import {
  getPresetColorScheme,
  isPresetTheme,
  THEME_PRESETS,
} from '@affine/core/modules/theme-presets';
import {
  DARK_CANVAS_SPOTLIGHT_GRADIENT,
  EDGELESS_GRID_BACKGROUND_IMAGE_VAR,
  LIGHT_CANVAS_SPOTLIGHT_GRADIENT,
} from '@blocksuite/affine-block-surface';
import {
  edgelessCanvasBackgroundVar,
  edgelessCanvasGridColorVar,
  edgelessLineGridPaletteStyle,
  minimapControlColorVar,
  minimapPanelBackgroundVar,
} from '@blocksuite/affine-shared/styles';
import { useLiveData, useServices } from '@toeverything/infra';
import { useTheme } from 'next-themes';
import { useEffect } from 'react';

// E016-S003: Page Mode Override CSS for hybrid themes
// Problem: --streamlain-background-primary-color is used by both Edgeless Canvas (should be dark)
// AND Page Mode Viewport (should be light). We need to override Page Mode specifically.
const PAGE_MODE_OVERRIDE_ID = 'sl-page-mode-override';
const EDGELESS_PRESET_OVERRIDE_ID = 'sl-edgeless-preset-override';
const STRONG_CHIP_LABEL_OVERRIDE_ID = 'sl-strong-chip-label-override';
const BLUE_WHITE_MINIMAP_CONTROL_COLOR = '#77757d';
const BLUE_WHITE_CANVAS_BACKGROUND_COLOR = '#213858';
const BLUE_WHITE_CANVAS_GRID_COLOR = '#0A1929';
const BLUE_WHITE_CANVAS_CARD_SHADOW =
  '0 -1px 8px rgba(80, 144, 211, 0.12), 0 6px 12px rgba(2, 10, 22, 0.48), 0 18px 38px rgba(2, 10, 22, 0.58)';
const DARK_CANVAS_BACKGROUND_COLOR = '#272A23';
const DARK_CANVAS_GRID_COLOR = '#20231D';
const LIGHT_CANVAS_BACKGROUND_COLOR = '#F3EFE5';
const LIGHT_CANVAS_GRID_COLOR = '#D2C19A';

const LIGHT_DATABASE_VIEWPORT_VARIABLES = `
      color: #141414;
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f5f5f5;
      --streamlain-background-tertiary-color: #ebebeb;
      --streamlain-background-overlay-panel-color: rgba(0, 0, 0, 0.6);
      --streamlain-background-error-color: #fdecea;
      --streamlain-error-color: #eb4335;
      --streamlain-success-color: #16a34a;
      --streamlain-warning-color: #f59e0b;
      --streamlain-border-color: #e3e2e4;
      --streamlain-divider-color: #e3e2e4;
      --streamlain-hover-color: #f5f5f5;
      --streamlain-icon-color: #77757d;
      --streamlain-placeholder-color: #c0bfc1;
      /* V2 tokens — kanban cards, table header icons, tooltips,
         popovers etc. read V2 layer / text / icon tokens directly. The
         preset sets them dark navy on :root so any descendant of the
         light database viewport would inherit dark without this scope. */
      --streamlain-v2-layer-background-primary: #ffffff;
      --streamlain-v2-layer-background-secondary: #f4f4f5;
      --streamlain-v2-layer-background-tertiary: #ebebeb;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-layer-insideBorder-blackBorder: #e6e6e6;
      --streamlain-v2-database-border: #e3e2e4;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-text-placeholder: #c0bfc1;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-button-grabber-default: #c0bfc1;
      --streamlain-v2-chip-label-blue: #2563eb;
      --streamlain-v2-chip-label-orange: #f97316;
      --streamlain-v2-chip-label-green: #22c55e;
      --streamlain-v2-chip-label-magenta: #c026d3;
      --streamlain-v2-chip-label-purple: #7c3aed;
      --streamlain-v2-chip-label-red: #ef4444;
      --streamlain-v2-chip-label-teal: #14b8a6;
      --streamlain-v2-chip-label-yellow: #eab308;
      --streamlain-v2-chip-label-grey: #475569;
      --streamlain-v2-chip-label-white: #e5e7eb;
      --streamlain-v2-chip-label-text: #ffffff;
`;

const LIGHT_PAGE_VIEWPORT_VARIABLES = `
      color: #141414;
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f5f5f5;
      --streamlain-background-tertiary-color: #ebebeb;
      --streamlain-border-color: #e3e2e4;
      --streamlain-divider-color: #e3e2e4;
      --streamlain-hover-color: #f5f5f5;
      --streamlain-icon-color: #77757d;
      --streamlain-placeholder-color: #c0bfc1;
      --streamlain-background-error-color: #fdecea;
      --streamlain-error-color: #eb4335;
      --streamlain-success-color: #16a34a;
      --streamlain-warning-color: #f59e0b;
      --streamlain-v2-layer-background-primary: #ffffff;
      --streamlain-v2-layer-background-secondary: #f4f4f5;
      --streamlain-v2-layer-background-tertiary: #ebebeb;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-text-placeholder: #c0bfc1;
      --streamlain-v2-icon-primary: #77757d;
`;

// Global raise of the select / multi-select option palette.
//
// Upstream `@streamlain/theme` ships `chip/label/*` tokens at very
// pastel saturation (#e6f5ff blue, #fce5e6 red, etc.) so a tinted
// chip reads as "a colored accent behind dark text". That looks
// right *as a chip* — but the same tokens are now reused by the
// gantt bar renderer as the bar's own background (bar = identity
// layer, status drives colour). Pastels rendered as washed-out
// watercolour on a full bar.
//
// Raised to the Tailwind -600 line so chips feel like the saturated
// Linear / Asana kanban-header pills (deep purple "Testing", magenta
// "Design Review", amber "Needs Update", emerald "Complete"). At
// -600 the luminance is low enough for crisp white labels to read
// on top, yet the hue identity stays obvious.
//
// The paired `color: #fff` + `font-weight: 600` on chip elements
// below is what keeps tag/chip text legible on these darker fills.
// Yellow is the odd one out — #ca8a04 is still light enough that
// white text sits at ~3:1 contrast (AA-fail). We special-case it
// with dark text via `affine-multi-tag-view` descendant override.
//
// Dark mode already ships saturated values (#004b7b blue,
// #460606 red, …) so no change there.
const STRONG_CHIP_LABEL_CSS = `
  :root,
  html[data-theme="light"]:root,
  html[data-theme="dark"]:root,
  html[data-theme="blue-white"]:root {
    /* Deep royal blue — In Progress maps here via chip/label/blue.
       Deliberately NOT the streamlain primary (#64B5F6 / #5090D3) — status
       chips must read as a semantic overlay, distinct from brand chrome.
       #2563eb (tailwind blue-600) is clearly darker and more saturated
       than the primary accent, avoiding "is this a status or a button?"
       ambiguity. */
    --streamlain-v2-chip-label-blue: #2563eb;

    /* Warm, friendly orange — picks up "fresh / pick me up" for Todo
       chips seeded by the wizard. */
    --streamlain-v2-chip-label-orange: #f97316;

    /* Vibrant green — completion / "ok" tone for Done. */
    --streamlain-v2-chip-label-green: #22c55e;

    /* Fuchsia, violet-leaning — magenta without sliding into red.
       Used as the Blocked badge so it reads as "attention needed"
       rather than "error". */
    --streamlain-v2-chip-label-magenta: #c026d3;

    /* Remaining palette — chosen so any user-added option still
       lands a saturated, appealing hue on the kanban / gantt
       surfaces (no washed-out pastels). */
    --streamlain-v2-chip-label-purple: #7c3aed;
    --streamlain-v2-chip-label-red: #ef4444;
    --streamlain-v2-chip-label-teal: #14b8a6;
    --streamlain-v2-chip-label-yellow: #eab308;
    --streamlain-v2-chip-label-grey: #475569;
    --streamlain-v2-chip-label-white: #e5e7eb;

    /* Chip foreground token — every chip in the raised palette is a
       saturated hue, so its overlay (label text, delete icon, etc.)
       reads cleanly only in white. Upstream ships a dark chip/label/text
       (#141414-ish) that made the close-X bleed into the orange/green
       backgrounds; pinning this to #ffffff at the token layer fixes
       every chip-label consumer without hunting down each render site. */
    --streamlain-v2-chip-label-text: #ffffff;
  }

  /* Chip text/icon inline styles keep labels white. Target only svg paths
     inside database light viewports so picker icons stay readable without
     blanket-overriding every descendant element. */
  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-select svg path,
  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-view svg path,
  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) data-view-group-title-select-view svg path,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-select svg path,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-view svg path,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) data-view-group-title-select-view svg path {
    color: #ffffff;
  }

  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-select input::placeholder,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-select input::placeholder {
    color: #929292;
  }

  /* Kanban lane header chips, card-cell chips, and group-by panel chips
     all render on saturated backgrounds and need white text. Scope this to
     data-view light viewports and portal group menus only. */
  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) data-view-group-title-select-view .tag,
  html[data-theme="light"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-view .select-selected,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) data-view-group-title-select-view .tag,
  html[data-theme="blue-white"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-multi-tag-view .select-selected,
  html[data-theme="light"] .blocksuite-portal data-view-group-title-select-view .tag,
  html[data-theme="blue-white"] .blocksuite-portal data-view-group-title-select-view .tag {
    color: #ffffff;
  }
`;

// Global CSS for Edgeless presets (applies regardless of App theme)
// Line-grid tiers have their own palette; dot/cross colors stay independent.
const EDGELESS_BLUE_WHITE_CSS = `
  /* Edgeless Light Canvas - subtle warm dots */
  .affine-edgeless-viewport[data-edgeless-preset="light"],
  html[data-theme="light"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasBackgroundVar}: ${LIGHT_CANVAS_BACKGROUND_COLOR};
    ${edgelessCanvasGridColorVar}: ${LIGHT_CANVAS_GRID_COLOR};
    ${edgelessLineGridPaletteStyle('light')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
  }

  .affine-edgeless-viewport[data-edgeless-preset="light"] .edgeless-background,
  html[data-theme="light"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image:
      ${LIGHT_CANVAS_SPOTLIGHT_GRADIENT},
      var(
        ${EDGELESS_GRID_BACKGROUND_IMAGE_VAR},
        radial-gradient(
          var(${edgelessCanvasGridColorVar}) 1px,
          var(${edgelessCanvasBackgroundVar}) 1px
        )
      );
  }

  /* Edgeless Dark Canvas - green-tinted graphite with darker grid marks */
  .affine-edgeless-viewport[data-edgeless-preset="dark"],
  html[data-theme="dark"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasBackgroundVar}: ${DARK_CANVAS_BACKGROUND_COLOR};
    ${edgelessCanvasGridColorVar}: ${DARK_CANVAS_GRID_COLOR};
    ${edgelessLineGridPaletteStyle('dark')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
  }

  .affine-edgeless-viewport[data-edgeless-preset="dark"] .edgeless-background,
  html[data-theme="dark"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image:
      ${DARK_CANVAS_SPOTLIGHT_GRADIENT},
      var(
        ${EDGELESS_GRID_BACKGROUND_IMAGE_VAR},
        radial-gradient(
          var(${edgelessCanvasGridColorVar}) 1px,
          var(${edgelessCanvasBackgroundVar}) 1px
        )
      );
  }

  /* Edgeless Blue-White Canvas - when app theme is blue-white and doc is automatic */
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"],
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    --streamlain-note-shadow-box: ${BLUE_WHITE_CANVAS_CARD_SHADOW};
    --streamlain-canvas-card-shadow: var(--streamlain-note-shadow-box);
  }

  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] {
    ${edgelessCanvasBackgroundVar}: ${BLUE_WHITE_CANVAS_BACKGROUND_COLOR};
    ${edgelessCanvasGridColorVar}: ${BLUE_WHITE_CANVAS_GRID_COLOR};
    ${edgelessLineGridPaletteStyle('blue-white')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
    ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
  }

  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] .edgeless-background {
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image:
      ${DARK_CANVAS_SPOTLIGHT_GRADIENT},
      var(
        ${EDGELESS_GRID_BACKGROUND_IMAGE_VAR},
        radial-gradient(
          var(${edgelessCanvasGridColorVar}) 1px,
          var(${edgelessCanvasBackgroundVar}) 1px
        )
      );
    background-size: 100% 100%, 20px 20px;
  }

  /* E016-S003: Edgeless Blue/White Canvas */
  /* IMPORTANT: Do NOT set --streamlain-background-primary-color here!
     Databases are children of .edgeless-background and would inherit it.
     Instead, set colors directly and override the background-image. */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    ${edgelessCanvasBackgroundVar}: ${BLUE_WHITE_CANVAS_BACKGROUND_COLOR};
    ${edgelessCanvasGridColorVar}: ${BLUE_WHITE_CANVAS_GRID_COLOR};
    ${edgelessLineGridPaletteStyle('blue-white')}
    ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
    ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
  }

  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] .edgeless-background {
    position: relative;
    background-color: var(${edgelessCanvasBackgroundVar});
    background-image:
      ${DARK_CANVAS_SPOTLIGHT_GRADIENT},
      var(
        ${EDGELESS_GRID_BACKGROUND_IMAGE_VAR},
        radial-gradient(
          var(${edgelessCanvasGridColorVar}) 1px,
          var(${edgelessCanvasBackgroundVar}) 1px
        )
      );
    background-size: 100% 100%, 20px 20px;
  }

  /* Frame borders in Blue/White - use dark-theme values (white alpha on dark bg) */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] {
    --streamlain-v2-edgeless-frame-border-default: #ffffff2b;
  }

  /* Notes in Blue/White mode - ensure Light theme styling */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-note {
    --streamlain-background-primary-color: #ffffff;
    --streamlain-background-secondary-color: #f5f5f5;
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
    --streamlain-border-color: #e3e2e4;
    --streamlain-icon-color: #77757d;
  }

  /* Frame Preview in Sidebar - ensure Light theme for text content */
  /* The frame-preview inherits dark theme vars - text needs to be readable */
  html[data-theme="blue-white"] .frame-preview-viewport {
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
  }

  /* Frame Preview - Database blocks need light theme styling */
  /* These would otherwise inherit dark/transparent backgrounds from blue-white theme */
  html[data-theme="blue-white"] .frame-preview-viewport affine-edgeless-database,
  html[data-theme="blue-white"] .frame-preview-viewport affine-database {
    --streamlain-background-primary-color: #ffffff;
    --streamlain-background-secondary-color: #f5f5f5;
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
    --streamlain-border-color: #e3e2e4;
    --streamlain-icon-color: #77757d;
  }

  /* Frame Preview - Automation blocks need light theme styling */
  html[data-theme="blue-white"] .frame-preview-viewport affine-automation,
  html[data-theme="blue-white"] .frame-preview-viewport [data-block-flavour="affine:automation"] {
    --streamlain-background-primary-color: #ffffff;
    --streamlain-background-secondary-color: #f5f5f5;
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
    --streamlain-border-color: #e3e2e4;
    --streamlain-icon-color: #77757d;
  }

  /* Frame Preview - Notes need light theme styling */
  html[data-theme="blue-white"] .frame-preview-viewport affine-edgeless-note {
    --streamlain-background-primary-color: #ffffff;
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
  }

  /* Kanban group headers in edgeless - V2 vars for Shadow DOM (group-title.ts) */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-database affine-data-view-kanban-group,
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] affine-edgeless-database affine-data-view-kanban-group {
    --streamlain-v2-text-primary: #141414;
    --streamlain-v2-text-secondary: #424149;
  }

  /* Live database views on edgeless nodes use the same DataView renderer
     as fullscreen. Keep its theme scope identical even when the surrounding
     canvas preset is dark. Scope to the viewport root and explicit nested
     theme boundaries; no descendant wildcard, because edgeless widgets such
     as automation cards and minimap must keep their own theme tokens. */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-database .database-live-presentation-viewport,
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-database .database-live-presentation-viewport [data-theme],
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] affine-edgeless-database .database-live-presentation-viewport,
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] affine-edgeless-database .database-live-presentation-viewport [data-theme] {
${LIGHT_DATABASE_VIEWPORT_VARIABLES}
  }

  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-database .database-live-presentation-viewport,
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] affine-edgeless-database .database-live-presentation-viewport {
    background: #ffffff;
  }

  /* Databases in Blue/White mode - ensure Light theme styling */
  /* These are children of .edgeless-background, so we must explicitly set Light colors */
  .affine-edgeless-viewport[data-edgeless-preset="blue-white"] affine-edgeless-database,
  html[data-theme="blue-white"] .affine-edgeless-viewport[data-edgeless-preset="system"] affine-edgeless-database {
    --streamlain-background-primary-color: #ffffff;
    --streamlain-background-secondary-color: #f5f5f5;
    --streamlain-text-primary-color: #141414;
    --streamlain-text-secondary-color: #424149;
    --streamlain-border-color: #e3e2e4;
    --streamlain-icon-color: #77757d;
  }

`;

function getPageModeOverrideCSS(themeName: string): string | null {
  // Only blue-white needs Page Mode override (hybrid theme)
  if (themeName !== 'blue-white') {
    return null;
  }

  // Override Page Mode Viewport and Database Fullscreen to stay light
  // while keeping Edgeless Canvas dark.
  // IMPORTANT: Do NOT use * selectors as they would also affect Edgeless Mode.
  // The Edgeless viewport has its own data-theme attribute that controls its appearance.
  return `
    /* E016-S003: Page Mode stays light in hybrid themes */
    /* Page Mode viewport - should be white, with scoped CSS variables */
    /* IMPORTANT: no descendant wildcard here. The viewport owns page-mode
       surface tokens; embedded islands keep their own scoped tokens. */
    html[data-theme="${themeName}"] .affine-page-viewport:not(.ai-answer-text-editor) {
${LIGHT_PAGE_VIEWPORT_VARIABLES}
    }

    html[data-theme="${themeName}"] .affine-page-viewport:not(.ai-answer-text-editor) {
      background: #ffffff;
    }

    /* Embedded page databases need the same light data-view tokens, but the
       scope must stop at database roots / explicit nested theme boundaries
       so automation nodes, minimaps, and other page widgets are untouched. */
    html[data-theme="${themeName}"] .affine-page-viewport affine-database,
    html[data-theme="${themeName}"] .affine-page-viewport affine-database [data-theme] {
${LIGHT_DATABASE_VIEWPORT_VARIABLES}
    }

    /* Kanban group headers in Page Mode - V2 vars for Shadow DOM (group-title.ts) */
    html[data-theme="${themeName}"] .affine-page-viewport affine-data-view-kanban-group {
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
    }

    /* NOTE: Frames Panel .presentation-button is fixed directly in frame-panel-header.ts */
    /* (Shadow DOM - CSS overrides from here don't work) */

    /* E018-S013: Database Fullscreen viewport - same light theme as Page Mode */
    /* The DatabaseFullscreenEditor uses affine-database which expects light theme variables */
    /* Apply to viewport roots and explicit nested theme boundaries. Avoid
       descendant wildcards; they override unrelated component-local styling. */
    html[data-theme="${themeName}"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport),
    html[data-theme="${themeName}"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) [data-theme] {
${LIGHT_DATABASE_VIEWPORT_VARIABLES}
    }
    html[data-theme="${themeName}"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) {
      background: #ffffff;
    }

    /* WorkflowFullscreenViewer mounts inside .affine-page-viewport, so
       the Page-Mode override above turned its Tailwind-blue toolbar and
       node picker white. Restore the blue-white preset tokens on the
       viewer root so toolbar / footer / menus keep the dark navy surface
       while the page viewport around it stays light. */
    html[data-theme="${themeName}"] .workflow-fullscreen-viewport {
${Object.entries(THEME_PRESETS['blue-white'] ?? {})
  .map(([k, v]) => `      ${k}: ${v};`)
  .join('\n')}
      ${edgelessCanvasBackgroundVar}: ${BLUE_WHITE_CANVAS_BACKGROUND_COLOR};
      ${edgelessCanvasGridColorVar}: ${BLUE_WHITE_CANVAS_GRID_COLOR};
      ${minimapPanelBackgroundVar}: var(${edgelessCanvasBackgroundVar});
      ${minimapControlColorVar}: ${BLUE_WHITE_MINIMAP_CONTROL_COLOR};
    }

    /* Notification toasts in Blue/White - use dark-mode styling */
    html[data-theme="${themeName}"] [data-testid="affine-toast"] {
      background: rgba(234, 234, 234, 1);
      color: #141414;
    }
    html[data-theme="${themeName}"] [data-testid="notification-center-viewport"] > div,
    html[data-theme="${themeName}"] [class*="notificationContent"] {
      --streamlain-white: rgb(30, 30, 30);
      --streamlain-black-10: rgba(255, 255, 255, 0.1);
      --streamlain-text-primary-color: #E3E2E4;
      --streamlain-text-secondary-color: #8E8D91;
      --streamlain-border-color: rgba(255, 255, 255, 0.1);
      --streamlain-hover-color: rgba(255, 255, 255, 0.08);
      --streamlain-processing-color: #1e96eb;
      --streamlain-shadow-1: 0px 0px 12px rgba(0, 0, 0, 0.5);
    }

    /* E016-S003: Comment Editor - ensure dark theme variables for BlockSuite editor inside */
    /* The PageEditor sets data-theme="light" internally (from ThemeProvider), so we need high specificity */
    /* to override the light theme variables for the comment editor in blue-white mode */
    html[data-theme="${themeName}"] .comment-editor-viewport,
    html[data-theme="${themeName}"] .comment-editor-viewport .page-editor-container,
    html[data-theme="${themeName}"] .comment-editor-viewport [data-theme="light"] {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-placeholder-color: #8696A7;
      --streamlain-black-30: rgba(255, 255, 255, 0.3);
      --streamlain-icon-color: #B2BAC2;
      --streamlain-background-primary-color: transparent;
    }

    /* E016-S003: Avatar in Comment Sidebar - target the sidebar container */
    html[data-theme="${themeName}"] [class*="commentItem"],
    html[data-theme="${themeName}"] [class*="pendingComment"],
    html[data-theme="${themeName}"] [class*="userContainer"] {
      --streamlain-v2-portrait-localPortraitBackground: #ffffff2b;
    }

    /* E016-S003: AI Error messages in chat sidebar - visible on dark background */
    html[data-theme="${themeName}"] ai-error-wrapper,
    html[data-theme="${themeName}"] .error-wrapper {
      --streamlain-v2-aI-errorBackground: rgba(244, 67, 54, 0.15);
      --streamlain-v2-aI-errorText: #ff8a80;
      --streamlain-v2-aI-errorDetailBackground: rgba(0, 0, 0, 0.2);
      --streamlain-v2-text-primary: #ffffff;
      --streamlain-v2-button-secondary: rgba(255, 255, 255, 0.1);
      --streamlain-v2-button-innerBlackBorder: rgba(255, 255, 255, 0.1);
    }

    /* E016-S003: User Messages in Chat Sidebar - ensure proper contrast on dark background */
    /* User message bubble has dark blue background (#132F4C), text should be white */
    html[data-theme="${themeName}"] chat-message-user,
    html[data-theme="${themeName}"] chat-message-user .chat-message-user {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-text-secondary-color: #B2BAC2;
    }

    /* User message text content - the bubble itself */
    html[data-theme="${themeName}"] chat-content-pure-text,
    html[data-theme="${themeName}"] .chat-content-pure-text {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-border-color: rgba(255, 255, 255, 0.2);
    }

    /* User info row (avatar area, timestamps) */
    html[data-theme="${themeName}"] .user-info {
      --streamlain-text-primary-color: #FFFFFF;
    }

    /* Chat messages container - placeholder text, onboarding items */
    html[data-theme="${themeName}"] ai-chat-messages .messages-placeholder-title,
    html[data-theme="${themeName}"] ai-chat-messages .onboarding-item-text {
      --streamlain-text-primary-color: #FFFFFF;
    }

    html[data-theme="${themeName}"] ai-chat-messages .onboarding-item-icon {
      --streamlain-text-secondary-color: #B2BAC2;
    }

    /* Down scroll indicator - needs visible background on dark sidebar */
    html[data-theme="${themeName}"] ai-chat-messages .down-indicator {
      --streamlain-background-primary-color: #1E3A5F;
      --streamlain-border-color: rgba(255, 255, 255, 0.2);
    }

    /* E016-S003: Assistant Messages in Chat Sidebar - ensure proper contrast on dark background */
    /* Assistant messages render rich text, code blocks, and action buttons */
    html[data-theme="${themeName}"] chat-message-assistant {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-placeholder-color: #8696A7;
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-text-secondary: #B2BAC2;
    }

    /* Assistant message info text ("with your docs", etc.) */
    html[data-theme="${themeName}"] chat-message-assistant .message-info {
      --streamlain-placeholder-color: #8696A7;
    }

    /* Rich text content in assistant messages */
    html[data-theme="${themeName}"] chat-content-rich-text,
    html[data-theme="${themeName}"] chat-content-stream-objects {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-text-link: #64B5F6;
      --streamlain-black-30: rgba(255, 255, 255, 0.3);
    }

    /* Code blocks in AI chat - ensure readable text on dark background */
    html[data-theme="${themeName}"] chat-content-rich-text pre,
    html[data-theme="${themeName}"] chat-content-rich-text code,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs,
    html[data-theme="${themeName}"] chat-content-rich-text [class*="code"],
    html[data-theme="${themeName}"] chat-message-assistant pre,
    html[data-theme="${themeName}"] chat-message-assistant code {
      --streamlain-v2-layer-background-codeBlock: #0D2137;
      --streamlain-code-background: #0D2137;
      background-color: #0D2137 !important;
      color: #E3E3E3 !important;
      --streamlain-text-primary-color: #E3E3E3;
    }

    /* Code block line numbers - remove white background */
    html[data-theme="${themeName}"] chat-content-rich-text .line-number,
    html[data-theme="${themeName}"] chat-message-assistant .line-number {
      background: transparent;
    }

    /* Inline code styling */
    html[data-theme="${themeName}"] chat-content-rich-text code:not(pre code),
    html[data-theme="${themeName}"] chat-message-assistant code:not(pre code) {
      background-color: rgba(13, 33, 55, 0.8) !important;
      color: #7EB8F0 !important;
      padding: 2px 6px;
      border-radius: 4px;
    }

    /* Code block syntax highlighting - light colors for dark background */
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-keyword,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-selector-tag,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-built_in {
      color: #7EB8F0 !important;
    }
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-string,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-attr {
      color: #98D89A !important;
    }
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-number,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-literal {
      color: #FFB86C !important;
    }
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-comment {
      color: #6B7D8F !important;
    }
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-function,
    html[data-theme="${themeName}"] chat-content-rich-text .hljs-title {
      color: #82AAFF !important;
    }

    /* Copy/More button and dropdown */
    html[data-theme="${themeName}"] chat-copy-more {
      --streamlain-hover-color: rgba(255, 255, 255, 0.08);
      --streamlain-v2-icon-primary: #B2BAC2;
      --streamlain-background-overlay-panel-color: #132F4C;
    }

    /* Action list buttons */
    html[data-theme="${themeName}"] chat-action-list {
      --streamlain-border-color: rgba(255, 255, 255, 0.15);
      --streamlain-white-10: rgba(255, 255, 255, 0.1);
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-icon-color: #B2BAC2;
    }

    /* AI Loading indicator */
    html[data-theme="${themeName}"] ai-loading {
      --streamlain-primary-color: #64B5F6;
    }

    /* E016-S003: Action Messages - AI-generated actions (slides, mindmap, etc.) */
    html[data-theme="${themeName}"] chat-message-action {
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-text-secondary: #B2BAC2;
    }

    /* Action wrapper (callout-style container) */
    html[data-theme="${themeName}"] action-wrapper,
    html[data-theme="${themeName}"] [class*="action-wrapper"] {
      --streamlain-primary-color: #64B5F6;
      --streamlain-v2-block-callout-background-grey: rgba(255, 255, 255, 0.05);
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-text-secondary: #B2BAC2;
      --streamlain-border-color: rgba(255, 255, 255, 0.15);
    }

    /* Action text component */
    html[data-theme="${themeName}"] action-text {
      --streamlain-border-color: rgba(255, 255, 255, 0.15);
    }

    /* E016-S003: AI Title (chat header) */
    html[data-theme="${themeName}"] ai-chat-panel-title {
      --streamlain-background-primary-color: #0A1929;
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-v2-icon-activated: #64B5F6;
    }

    /* E016-S003: Chat Input */
    html[data-theme="${themeName}"] ai-chat-input {
      --streamlain-v2-layer-insideBorder-border: rgba(255, 255, 255, 0.15);
      --streamlain-v2-input-background: rgba(255, 255, 255, 0.05);
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-text-primary-color: #FFFFFF;
      --streamlain-border-color: rgba(255, 255, 255, 0.15);
      --streamlain-white: #FFFFFF;
      --streamlain-quote-color: rgba(255, 255, 255, 0.1);
      --streamlain-v2-icon-primary: #B2BAC2;
      --streamlain-v2-layer-background-hoverOverlay: rgba(255, 255, 255, 0.08);
      --streamlain-v2-icon-activated: #64B5F6;
      --streamlain-v2-icon-secondary: #8696A7;
      --streamlain-black-30: rgba(255, 255, 255, 0.3);
      --streamlain-v2-text-placeholder: #5A6A7A;
      --streamlain-v2-layer-insideBorder-primaryBorder: #64B5F6;
      --streamlain-v2-layer-pureWhite: #FFFFFF;
      --streamlain-v2-button-disable: rgba(255, 255, 255, 0.1);
    }

    /* E016-S003: Session History dropdown */
    html[data-theme="${themeName}"] ai-session-history {
      --streamlain-v2-layer-insideBorder-border: rgba(255, 255, 255, 0.15);
      --streamlain-v2-layer-background-primary: #0A1929;
      --streamlain-v2-layer-background-overlayPanel: #132F4C;
      --streamlain-overlay-panel-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
      --streamlain-text-secondary-color: #B2BAC2;
      --streamlain-v2-text-secondary: #B2BAC2;
      --streamlain-v2-layer-background-hoverOverlay: rgba(255, 255, 255, 0.08);
      --streamlain-v2-text-emphasis: #64B5F6;
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-icon-primary: #B2BAC2;
    }

    /* E016-S003: Chat Chips (context chips, add popover) */
    html[data-theme="${themeName}"] chat-panel-chips,
    html[data-theme="${themeName}"] ai-chat-chip,
    html[data-theme="${themeName}"] chat-chips-add-popover,
    html[data-theme="${themeName}"] chat-chip-candidates-popover {
      --streamlain-v2-layer-insideBorder-border: rgba(255, 255, 255, 0.15);
      --streamlain-v2-layer-background-primary: #0A1929;
      --streamlain-v2-layer-background-secondary: rgba(255, 255, 255, 0.05);
      --streamlain-v2-layer-background-overlayPanel: #132F4C;
      --streamlain-v2-layer-background-hoverOverlay: rgba(255, 255, 255, 0.08);
      --streamlain-v2-layer-background-error: rgba(244, 67, 54, 0.15);
      --streamlain-v2-icon-primary: #B2BAC2;
      --streamlain-v2-icon-secondary: #8696A7;
      --streamlain-v2-icon-tertiary: #5A6A7A;
      --streamlain-v2-text-primary: #FFFFFF;
      --streamlain-v2-text-secondary: #B2BAC2;
      --streamlain-v2-text-placeholder: #5A6A7A;
      --streamlain-v2-status-error: #ff8a80;
      --streamlain-overlay-panel-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
    }

    /* E016-S003: Notification Center (Sonner toasts) - use light theme colors */
    /* The notification uses --streamlain-text-primary-color and --streamlain-white which are white in blue-white */
    /* Override to use dark text on light background for readability */
    html[data-theme="${themeName}"] .affine-notification-center,
    html[data-theme="${themeName}"] .affine-notification-center * {
      --streamlain-text-primary-color: #141414;
      --streamlain-white: #ffffff;
      --streamlain-pure-white: #ffffff;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-border-color: #e3e2e4;
      --streamlain-icon-color: #77757d;
    }

    /* E024: Database Menus in Blue/White Theme - ensure light theme for portaled menus */
    /* affine-menu elements render as portals outside the database DOM, so they inherit */
    /* the dark theme variables from body. Override to use light theme colors. */
    html[data-theme="${themeName}"] affine-menu {
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f4f4f5;
      --streamlain-background-tertiary-color: #ebebeb;
      --streamlain-background-error-color: #fdecea;
      --streamlain-error-color: #eb4335;
      --streamlain-success-color: #16a34a;
      --streamlain-warning-color: #f59e0b;
      --streamlain-border-color: #e3e2e4;
      --streamlain-icon-color: #77757d;
      --streamlain-hover-color: #00000008;
      --streamlain-placeholder-color: #c0bfc1;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #8e8d91;
      --streamlain-v2-text-placeholder: #c0bfc1;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-icon-disable: #c0bfc1;
      --streamlain-v2-layer-background-primary: #ffffff;
      --streamlain-v2-layer-background-secondary: #f4f4f5;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-input-border-default: #e3e2e4;
      --streamlain-v2-button-buttonOverHover: #00000008;
    }

    /* E024: React menus (Radix popover) inside database cells — force light theme */
    /* These portaled menus inherit dark theme vars from body. The .sl-db-menu */
    /* class is set via contentOptions on <Menu> components in database cells. */
    html[data-theme="${themeName}"] .sl-db-menu {
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-hover-color: #00000008;
      --streamlain-background-error-color: #fdecea;
      --streamlain-error-color: #eb4335;
      --streamlain-success-color: #16a34a;
      --streamlain-warning-color: #f59e0b;
      --streamlain-menu-shadow: 0px 0px 12px rgba(66, 65, 73, 0.14);
    }

    /* Sub-menus and date pickers */
    html[data-theme="${themeName}"] affine-sub-menu,
    html[data-theme="${themeName}"] affine-date-picker {
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-text-primary: #141414;
    }

    /* Data-view popup containers (tag selectors, filters, etc.) */
    /* NOTE: Only target .data-view-popup-container, NOT all [data-radix-popper-content-wrapper] */
    /* Sidebar menus (user menu, workspace selector) should stay dark blue with white text */
    html[data-theme="${themeName}"] .data-view-popup-container {
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #8e8d91;
    }

    /* E024: Multi-Tag Select Popup - the actual Select/Multi-Select column popup */
    /* This is a portaled element (affine-multi-tag-select) that renders outside the database DOM */
    /* It uses cssVarV2 variables which inherit dark theme values - override to light theme */
    html[data-theme="${themeName}"] affine-multi-tag-select {
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-placeholder-color: #c0bfc1;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-shadow-1: 0px 0px 10px rgba(0, 0, 0, 0.1);
      /* cssVarV2 variables used by styles-css.ts */
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #8e8d91;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-layer-insideBorder-blackBorder: #e6e6e6;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-database-border: #e3e2e4;
      --streamlain-v2-chip-label-text: #ffffff;
      --streamlain-v2-button-grabber-default: #c0bfc1;
    }

    /* E029: Document Card View (All Docs tile view) - ensure readable text on light cards */
    /* Card tiles use light background but inherit white text from blue-white theme */
    /* Override to use dark text for readability */
    html[data-theme="${themeName}"] [class*="cardViewRoot"],
    html[data-theme="${themeName}"] [class*="cardViewTitle"],
    html[data-theme="${themeName}"] [class*="cardPreviewContainer"] {
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-layer-background-mobile-secondary: #ffffff;
      --streamlain-v2-layer-insideBorder-border: #e3e2e4;
      --streamlain-v2-pagelist-hoverBorder: #c0bfc1;
    }

    /* Kanban group headers in data-view light viewports - V2 vars for Shadow DOM (group-title.ts) */
    html[data-theme="${themeName}"] :is(.database-fullscreen-viewport, .database-live-presentation-viewport) affine-data-view-kanban-group {
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
    }

    /* Detail panel modals - light theme for database record detail */
    /* Two rendering paths: (1) PeekView Radix Dialog.Portal [data-peek-view-wrapper] */
    /* (2) Fallback popSideDetail → createModal → center-peek element at <body> level */
    /* Both render OUTSIDE .database-fullscreen-viewport, so viewport overrides don't apply */
    html[data-theme="${themeName}"] [data-peek-view-wrapper],
    html[data-theme="${themeName}"] center-peek {
      color: #141414;
      background-color: #ffffff;
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f5f5f5;
      --streamlain-border-color: #e3e2e4;
      --streamlain-hover-color: #f5f5f5;
      --streamlain-icon-color: #77757d;
      --streamlain-placeholder-color: #c0bfc1;
      --streamlain-primary-color: #5090D3;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
    }

    /* Close button sits outside the white panel (right: -32px) on the dark overlay */
    html[data-theme="${themeName}"] center-peek .close-modal {
      color: #141414;
      background-color: #ffffff;
      --streamlain-hover-color: #f5f5f5;
    }

    /* Drag preview for Kanban/Table cards - ensure light theme when dragged outside scoped viewport */
    /* Drag previews are appended to document.body, outside .affine-page-viewport scope */
    /* Must override ALL theme-dependent CSS variables used by KanbanCard, TableRow, and their cells */
    html[data-theme="${themeName}"] .data-view-drag-preview,
    html[data-theme="${themeName}"] .data-view-drag-preview * {
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f5f5f5;
      --streamlain-background-tertiary-color: #ebebeb;
      --streamlain-background-kanban-card-color: #ffffff;
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-border-color: #e3e2e4;
      --streamlain-divider-color: #e3e2e4;
      --streamlain-icon-color: #77757d;
      --streamlain-hover-color: #f5f5f5;
      --streamlain-placeholder-color: #c0bfc1;
      --streamlain-primary-color: #5090D3;
      --streamlain-black-30: rgba(0, 0, 0, 0.3);
      --streamlain-shadow-2: 0px 0px 12px rgba(66, 65, 73, 0.14), 0px 0px 0px 0.5px rgba(0, 0, 0, 0.1);
      --streamlain-kanban-shadow: 0px 2px 3px 0px rgba(0, 0, 0, 0.05);
      --streamlain-font-family: Inter, -apple-system, BlinkMacSystemFont, sans-serif;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-text-placeholder: #c0bfc1;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-layer-background-primary: #ffffff;
      --streamlain-v2-layer-background-secondary: #f4f4f5;
      --streamlain-v2-layer-background-hoverOverlay: #00000008;
      color: #141414;
    }

    /* E029: AI Island FAB hover - brighten on hover instead of darken */
    /* The default hover color is dark (#132F4C) which makes the button look darker on dark background */
    /* Override to use a lighter overlay that brightens the button on hover */
    html[data-theme="${themeName}"] [class*="aiIslandBtn"]:hover::after {
      background: rgba(255, 255, 255, 0.15) !important;
    }
    /* Make icon white on hover - override hardcoded SVG fill */
    html[data-theme="${themeName}"] [class*="aiIslandBtn"]:hover svg path {
      fill: #FFFFFF !important;
    }

    /* Floating popovers for data-view mount under document.body via
       createLitPortal, gantt-tooltip append, etc., so they render
       OUTSIDE the \`.database-fullscreen-viewport\` scope. Without
       this block they inherit the dark blue-white palette.

       IMPORTANT — date-picker lives inside the portal's open
       shadow DOM (blocksuite-portal → #shadow-root → date-picker).
       External selectors cannot pierce shadow DOM, so targeting
       \`date-picker\` directly does nothing. Instead we set the CSS
       custom properties on the shadow host (\`.blocksuite-portal\`) —
       CSS custom properties DO inherit across shadow-root
       boundaries, so the date-picker's internal styles pick them
       up via normal var() lookup.

       Gantt tooltips use ShadowlessElement and render straight in
       light DOM, so their tag selectors work as-is. */
    html[data-theme="${themeName}"] .blocksuite-portal,
    html[data-theme="${themeName}"] dv-gantt-bar-hover-tooltip,
    html[data-theme="${themeName}"] dv-gantt-bar-hover-tooltip *,
    html[data-theme="${themeName}"] dv-gantt-drag-tooltip,
    html[data-theme="${themeName}"] dv-gantt-drag-tooltip * {
      --streamlain-background-primary-color: #ffffff;
      --streamlain-background-secondary-color: #f5f5f5;
      --streamlain-background-tertiary-color: #ebebeb;
      --streamlain-background-overlay-panel-color: #ffffff;
      --streamlain-text-primary-color: #141414;
      --streamlain-text-secondary-color: #424149;
      --streamlain-text-disable-color: #8e8d91;
      --streamlain-border-color: #e3e2e4;
      --streamlain-divider-color: #e3e2e4;
      --streamlain-hover-color: #f5f5f5;
      --streamlain-icon-color: #77757d;
      --streamlain-icon-hover-color: #141414;
      --streamlain-icon-hover-background: rgba(0, 0, 0, 0.04);
      --streamlain-primary-color: #5090D3;
      --streamlain-pure-white: #ffffff;
      --streamlain-menu-shadow: 0px 0px 12px rgba(66, 65, 73, 0.14);
      --streamlain-v2-layer-background-primary: #ffffff;
      --streamlain-v2-layer-background-overlayPanel: #ffffff;
      --streamlain-v2-layer-insideBorder-border: #e6e6e6;
      --streamlain-v2-text-primary: #141414;
      --streamlain-v2-text-secondary: #424149;
      --streamlain-v2-icon-primary: #77757d;
      --streamlain-v2-tooltips-background: #ffffff;
      --streamlain-v2-tooltips-foreground: #141414;
      color: #141414;
    }

  `;
}

let _provided = false;

export const CustomThemeModifier = () => {
  const { themeEditorService, featureFlagService, editorSettingService } =
    useServices({
      ThemeEditorService,
      FeatureFlagService,
      EditorSettingService,
    });
  const enableThemeEditor = useLiveData(
    featureFlagService.flags.enable_theme_editor.$
  );
  const settings = useLiveData(editorSettingService.editorSetting.settings$);
  const { resolvedTheme } = useTheme();

  // Handle preset themes
  useEffect(() => {
    // Clean up previous override styles first
    const existingOverride = document.getElementById(PAGE_MODE_OVERRIDE_ID);
    if (existingOverride) {
      existingOverride.remove();
    }

    if (!resolvedTheme || !isPresetTheme(resolvedTheme)) {
      return;
    }

    const presetVars = THEME_PRESETS[resolvedTheme];
    const colorScheme = getPresetColorScheme(resolvedTheme);

    // Clear previous styles and apply preset
    document.documentElement.style.cssText = '';
    document.documentElement.style.colorScheme = colorScheme;

    Object.entries(presetVars).forEach(([key, value]) => {
      value && document.documentElement.style.setProperty(key, value);
    });

    // E016-S003: Inject Page Mode override CSS for hybrid themes
    const overrideCSS = getPageModeOverrideCSS(resolvedTheme);
    if (overrideCSS) {
      const styleEl = document.createElement('style');
      styleEl.id = PAGE_MODE_OVERRIDE_ID;
      styleEl.textContent = overrideCSS;
      document.head.appendChild(styleEl);
    }

    // Cleanup function
    return () => {
      const styleEl = document.getElementById(PAGE_MODE_OVERRIDE_ID);
      if (styleEl) {
        styleEl.remove();
      }
    };
  }, [resolvedTheme]);

  // Handle standard themes (light/dark/system) with ThemeEditor support
  useEffect(() => {
    // Skip if preset theme is active
    if (resolvedTheme && isPresetTheme(resolvedTheme)) {
      return;
    }

    const mode = resolvedTheme === 'dark' ? 'dark' : 'light';
    document.documentElement.style.cssText = '';
    document.documentElement.style.colorScheme = mode;

    // Skip if ThemeEditor is disabled
    if (!enableThemeEditor) {
      return;
    }

    if (_provided) return;
    _provided = true;

    const sub = themeEditorService.customTheme$.subscribe(themeObj => {
      if (!themeObj) return;

      const valueMap = themeObj[mode];

      // remove previous style
      // TOOD(@CatsJuice): find better way to remove previous style
      document.documentElement.style.cssText = '';
      // recover color scheme set by next-themes
      document.documentElement.style.colorScheme = mode;

      Object.entries(valueMap).forEach(([key, value]) => {
        value && document.documentElement.style.setProperty(key, value);
      });
    });

    return () => {
      _provided = false;
      sub.unsubscribe();
    };
  }, [resolvedTheme, enableThemeEditor, themeEditorService]);

  // Apply font size CSS variable when settings change
  useEffect(() => {
    if (settings.fontSize) {
      document.documentElement.style.setProperty(
        '--streamlain-font-base',
        `${settings.fontSize}px`
      );
    }
  }, [settings.fontSize]);

  // E016-S003: Inject global Edgeless Blue/White CSS (always active, regardless of App theme)
  useEffect(() => {
    // Only inject once
    if (document.getElementById(EDGELESS_PRESET_OVERRIDE_ID)) {
      return;
    }

    const styleEl = document.createElement('style');
    styleEl.id = EDGELESS_PRESET_OVERRIDE_ID;
    styleEl.textContent = EDGELESS_BLUE_WHITE_CSS;
    document.head.appendChild(styleEl);

    // No cleanup - this CSS should persist for the entire session
  }, []);

  // Inject the raised chip-label palette once per session so select /
  // multi-select chips + gantt bar fills look saturated instead of
  // washed-out. Paired with the bar renderer dropping its color-mix
  // slate-darken step — the token itself is now strong enough.
  useEffect(() => {
    if (document.getElementById(STRONG_CHIP_LABEL_OVERRIDE_ID)) {
      return;
    }
    const styleEl = document.createElement('style');
    styleEl.id = STRONG_CHIP_LABEL_OVERRIDE_ID;
    styleEl.textContent = STRONG_CHIP_LABEL_CSS;
    document.head.appendChild(styleEl);
  }, []);

  return null;
};
